// Text that becomes an input on click. Enter, Save or leaving the field
// saves; Escape or Cancel gives the edit up, asking first when the text
// changed (touch has no Escape, hence the buttons). The caller's save is
// optimistic, so the new value shows at once; a refused save rolls back in
// the store and raises a toast.
import { useEffect, useLayoutEffect, useRef, type KeyboardEvent } from 'react'
import { cx } from '../transcript/bits'
import { confirmDiscard, useUnsavedChanges } from './unsaved'
import { useWorkspaceDraft } from '../workspace/drafts'
import { canInteract, useTabScope } from '../workspace/context'

export interface InlineTextProps {
  value: string
  onSave: (next: string) => void
  /** Shown when the value is empty. */
  placeholder: string
  /** Accessible name, e.g. "title". */
  name: string
  maxLength: number
  /** An empty value is refused (a title) or saved as cleared (a summary). */
  required?: boolean
  multiline?: boolean
  /** Open as a field, its text selected so typing replaces it. */
  initiallyEditing?: boolean
  /** Names the kept draft where several of one name share a page, e.g. the enlarged card's. */
  draftId?: string
  className?: string
  as?: 'h1' | 'h2' | 'p' | 'div'
}

/** The text a draft saves as: one line, trimmed. */
function clean(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

interface Edit {
  base: string
  draft: string
}

export function InlineText({
  value,
  onSave,
  placeholder,
  name,
  maxLength,
  required = false,
  multiline = false,
  initiallyEditing = false,
  draftId,
  className,
  as: Tag = 'div',
}: InlineTextProps) {
  const scope = useTabScope()
  const [edit, setEdit] = useWorkspaceDraft<Edit | null>(`inline:${draftId ?? name}`, initiallyEditing ? { base: value, draft: value } : null,
    (value): value is Edit | null => value === null || (
      typeof value === 'object' && value !== null
      && 'base' in value && typeof value.base === 'string'
      && 'draft' in value && typeof value.draft === 'string'
    ),
    edit => edit !== null && clean(edit.draft) !== clean(edit.base))
  const editing = edit !== null
  const draft = edit?.draft ?? value
  const field = useRef<HTMLTextAreaElement & HTMLInputElement>(null)
  // Enter, Escape or a button ends the edit; the blur that follows must not save again.
  const ended = useRef(false)
  const selectAll = useRef(initiallyEditing)
  const next = clean(draft)
  const dirty = edit !== null && next !== clean(edit.base)
  useUnsavedChanges(dirty)

  // In the commit that shows the field, as autoFocus does: a passive effect
  // runs a task later, and a key pressed meanwhile went elsewhere (Poulpe-800).
  useLayoutEffect(() => {
    if (!editing || !canInteract(scope)) return
    const el = field.current
    if (!el) return
    el.focus()
    if (selectAll.current) el.select()
    else el.setSelectionRange(el.value.length, el.value.length)
    selectAll.current = false
  }, [editing, scope])

  useEffect(() => {
    if (!multiline || !editing || !field.current) return
    const el = field.current
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [draft, editing, multiline])

  const start = () => {
    ended.current = false
    setEdit({ base: value, draft: value })
  }

  const commit = () => {
    if (ended.current || !canInteract(scope)) return
    ended.current = true
    if (!dirty || next === value.trim() || (required && !next)) {
      setEdit(null)
      return
    }
    if (edit && value !== edit.base
      && !window.confirm(`The ${name} changed while you were editing. Replace it?`)) {
      ended.current = false
      field.current?.focus()
      return
    }
    setEdit(null)
    onSave(next)
  }

  const cancel = () => {
    if (ended.current) return
    // The question takes the focus away; that blur must not save.
    ended.current = true
    if (dirty && !confirmDiscard()) {
      ended.current = false
      field.current?.focus()
      return
    }
    setEdit(null)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    // WebKit may end composition before its final keydown, still marked 229.
    if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      cancel()
    }
  }

  if (editing) {
    const common = {
      ref: field,
      className: cx('inline-input', className),
      value: draft,
      maxLength,
      'aria-label': `Edit ${name}`,
      onChange: (e: { target: { value: string } }) => setEdit({ base: edit!.base, draft: e.target.value }),
      onBlur: commit,
      onKeyDown,
    }
    return (
      <div className="inline-edit">
        {multiline ? <textarea rows={1} {...common} /> : <input type="text" {...common} />}
        {/* Pressing a button must not blur the field first: the blur would save. */}
        <div className="inline-edit-actions" onMouseDown={(e) => e.preventDefault()}>
          <button type="button" className="button" onClick={cancel}>
            Cancel
          </button>
          <button type="button" className="button button-primary" onClick={commit} disabled={required && !next}>
            Save
          </button>
        </div>
      </div>
    )
  }

  return (
    <Tag
      className={cx('inline-text', className, !value && 'is-empty')}
      role="button"
      tabIndex={0}
      aria-label={`${value || placeholder}. Edit ${name}`}
      onClick={start}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          start()
        }
      }}
    >
      {value || placeholder}
    </Tag>
  )
}
